<template>
  <PageHeader :showBreadcrumb="true"/>
  <form @submit.prevent="onSubmit" aria-label="External Appeal Form">
    <h2>Instructions</h2>
    <p>To assist you in addressing your External Appeal, you must complete all the sections below, and upload any
      supporting evidence that will help in the review of your appeal.
      After signing the Consent to Release on the last screen, you will receive an email with your appeal number after
      you submit it. Please keep this number for your records.</p>
    <p>After submitting your external appeal, MDH will review and then forward your appeal to a state-approved
      Independent Review Organization (IRO) contract vendor.</p>
    <p>Based on the information you supply in your application and uploads, the state-approved IRO will make the final
      decision on your external appeal.</p>
    <p>More information on External Appeals is available on the <a
      href="https://www.health.state.mn.us/facilities/insurance/managedcare/complaint/external.html">Enrollee External
      Appeal Process (health.mn.us)</a> website.
      If you have additional questions, please email <a href="mailto:health.mcs@state.mn.us">health.mcs@state.mn.us</a>
    </p>

    <hr/>
    <!-- Patient Section -->
    <fieldset>
      <legend>Patient Information</legend>

      <MnRow>
        <MnColumn cols="4">
          <ValidatedInput
            id="firstName"
            label="First Name"
            v-model="form.state.data.patient.firstName"
            :validation="form.state.$v.patient.firstName"
            :inputComponent="InputText"
            :inputAttrs="{ placeholder: 'First Name' }"
          />
        </MnColumn>
        <MnColumn cols="4">
          <ValidatedInput
            id="middleName"
            label="Middle Name"
            v-model="form.state.data.patient.middleName"
            :inputComponent="InputText"
            :inputAttrs="{ placeholder: 'Middle Name' }"
          />
        </MnColumn>
        <MnColumn cols="4">
          <ValidatedInput
            id="lastName"
            label="Last Name"
            v-model="form.state.data.patient.lastName"
            :validation="form.state.$v.patient.lastName"
            :inputComponent="InputText"
            :inputAttrs="{ placeholder: 'Last Name' }"
          />
        </MnColumn>
      </MnRow>

      <MnRow>
        <MnColumn cols="4">
          <ValidatedInput
            id="dob"
            label="Date of Birth"
            v-model="form.state.data.patient.dob"
            :validation="form.state.$v.patient.dob"
            :inputComponent="DatePicker"
            :inputAttrs="{
              inputId: 'dob',
              ariaLabel: 'Date of Birth',
              placeholder: 'MM/DD/YYYY',
              dateFormat: 'mm/dd/yy',
              showIcon: true,
              showButtonBar: true,
              class: 'form-control mb-2',
              maxDate: new Date()
            }"
          />
        </MnColumn>
        <MnColumn cols="4">
          <ValidatedInput
            id="phoneNumber"
            label="Phone Number"
            v-model="form.state.data.patient.phoneNumber"
            :inputComponent="InputMask"
            :inputAttrs="{ placeholder: 'Phone Number', mask: '(999) 999-9999' }"
          />
        </MnColumn>
        <MnColumn cols="4">
          <ValidatedInput
            id="email"
            label="Email"
            v-model="form.state.data.patient.email"
            :validation="form.state.$v.patient.email"
            :inputComponent="InputText"
            :inputAttrs="{ placeholder: 'Email' }"
          />
        </MnColumn>
      </MnRow>

      <MnRow>
        <MnColumn cols="4">
          <ValidatedInput
            id="relationshipToPatient"
            label="How are you related to the patient?"
            v-model="form.state.data.patient.relationshipToPatient"
            :validation="form.state.$v.patient.relationshipToPatient"
            :inputComponent="Select"
            :inputAttrs="{
              inputId: 'relationshipToPatient',
              ariaLabel: 'How are you related to the patient?',
              options: patientRelationshipOptions,
              optionLabel: 'value',
              optionValue: 'key',
              placeholder: 'Select Relationship'
            }"
          />
        </MnColumn>
        <MnColumn v-if="form.state.data.patient.relationshipToPatient && form.state.data.patient.relationshipToPatient === EXTERNAL_APPEALS_PATIENT_RELATIONSHIPS.Other" cols="4">
          <ValidatedInput
            id="otherRelationship"
            label="Description of relationship to Patient"
            v-model="form.state.data.patient.otherRelationship"
            :validation="form.state.$v.patient.otherRelationship"
            :inputComponent="InputText"
            :inputAttrs="{ placeholder: 'Description of relationship to Patient' }"
          />
        </MnColumn>
      </MnRow>
    </fieldset>

    <br/>

    <fieldset v-if="form.state.data.patient.relationshipToPatient && form.state.data.patient.relationshipToPatient !== EXTERNAL_APPEALS_PATIENT_RELATIONSHIPS.Self">
      <legend>Authorized Representative</legend>

      <MnRow>
        <MnColumn cols="4">
          <ValidatedInput
            id="authRepName"
            label="Your Name"
            v-model="form.state.data.authorizedRep.fullName"
            :validation="form.state.$v.authorizedRep.fullName"
            :inputComponent="InputText"
            :inputAttrs="{ placeholder: 'Your Full Name' }"
          />
        </MnColumn>

        <MnColumn cols="4">
          <ValidatedInput
            id="authRepPhone"
            label="Your Primary Phone Number"
            v-model="form.state.data.authorizedRep.phoneNumber"
            :validation="form.state.$v.authorizedRep.phoneNumber"
            :inputComponent="InputMask"
            :inputAttrs="{ placeholder: 'Phone Number', mask: '(999) 999-9999' }"
          />
        </MnColumn>

        <MnColumn cols="4">
          <ValidatedInput
            id="authRepEmail"
            label="Your Email Address"
            v-model="form.state.data.authorizedRep.email"
            :validation="form.state.$v.authorizedRep.email"
            :inputComponent="InputText"
            :inputAttrs="{ placeholder: 'Email Address' }"
          />
        </MnColumn>
      </MnRow>
    </fieldset>

    <br/>

    <fieldset>
      <legend>Patient Address</legend>
      <MnRow>
        <MnColumn cols="8">
          <ValidatedInput
            id="street"
            label="Street"
            v-model="form.state.data.patient.street"
            :validation="form.state.$v.patient.street"
            :inputComponent="InputText"
            :inputAttrs="{ placeholder: 'Street' }"
          />
        </MnColumn>
      </MnRow>

      <MnRow>
        <MnColumn cols="4">
          <ValidatedInput
            id="city"
            label="City"
            v-model="form.state.data.patient.city"
            :validation="form.state.$v.patient.city"
            :inputComponent="InputText"
            :inputAttrs="{ placeholder: 'City' }"
          />
        </MnColumn>

        <MnColumn cols="4">
          <ValidatedInput
            id="state"
            label="State"
            v-model="form.state.data.patient.state"
            :validation="form.state.$v.patient.state"
            :inputComponent="InputText"
            :inputAttrs="{ placeholder: 'State' }"
          />
        </MnColumn>

        <MnColumn cols="4">
          <ValidatedInput
            id="zipCode"
            label="Zip Code"
            v-model="form.state.data.patient.zipCode"
            :validation="form.state.$v.patient.zipCode"
            :inputComponent="InputMask"
            :inputAttrs="{ placeholder: 'Zip Code', mask: '99999?-9999' }"
          />
        </MnColumn>
      </MnRow>
    </fieldset>

    <br/>

    <fieldset>
      <legend>Health Plan Information</legend>

      <MnRow>
        <MnColumn cols="4">
          <ValidatedInput
            id="healthInsuranceCompany"
            label="Health Insurance Company"
            v-model="form.state.data.healthPlan.healthInsuranceCompany"
            :validation="form.state.$v.healthPlan.healthInsuranceCompany"
            :inputComponent="Select"
            :inputAttrs="{
              inputId: 'healthInsuranceCompany',
              ariaLabel: 'Health Insurance Company',
              options: healthInsuranceCompanyOptions,
              optionLabel: 'value',
              optionValue: 'key',
              placeholder: 'Select Health Insurance Company'
            }"
          />
        </MnColumn>

        <MnColumn v-if="form.state.data.healthPlan.healthInsuranceCompany && form.state.data.healthPlan.healthInsuranceCompany === EXTERNAL_APPEALS_HEALTH_INSURANCE_COMPANIES.Other" cols="4">
          <ValidatedInput
            id="otherHmoName"
            label="Other Health Insurance Company Name"
            v-model="form.state.data.healthPlan.otherHmoName"
            :validation="form.state.$v.healthPlan.otherHmoName"
            :inputComponent="InputText"
            :inputAttrs="{ placeholder: 'Health Insurance Company' }"
          />
        </MnColumn>

        <MnColumn cols="4">
          <ValidatedInput
            id="patientInsuranceId"
            label="Patient Insurance ID"
            v-model="form.state.data.healthPlan.patientInsuranceId"
            :validation="form.state.$v.healthPlan.patientInsuranceId"
            :inputComponent="InputText"
            :inputAttrs="{ placeholder: 'Insurance ID#' }"
          />
        </MnColumn>
      </MnRow>

      <MnRow>
        <MnColumn cols="4">
          <ValidatedInput
            id="typeOfCoverage"
            label="Type of Coverage"
            v-model="form.state.data.healthPlan.typeOfCoverage"
            :inputComponent="Select"
            :inputAttrs="{
              inputId: 'typeOfCoverage',
              ariaLabel: 'Type of Coverage',
              options: typeOfCoverageOptions,
              optionLabel: 'value',
              optionValue: 'key',
              placeholder: 'Select Coverage Type'
            }"
          />
        </MnColumn>

        <MnColumn v-if="form.state.data.healthPlan.typeOfCoverage && form.state.data.healthPlan.typeOfCoverage === EXTERNAL_APPEALS_HEALTH_INSURANCE_COMPANIES.Other" cols="4">
          <ValidatedInput
            id="otherHmoName"
            label="Other Type of Coverage"
            v-model="form.state.data.healthPlan.otherTypeOfCoverage"
            :inputComponent="InputText"
            :inputAttrs="{ placeholder: 'Type of Coverage' }"
          />
        </MnColumn>
      </MnRow>
    </fieldset>

    <br/>

    <fieldset>
      <legend>Summary of Appeal</legend>
      <MnRow>
        <MnColumn cols="12">
          <ValidatedInput
            id="summaryOfAppeal"
            label="Summary of Appeal"
            v-model="form.state.data.summaryOfAppeal"
            :validation="form.state.$v.summaryOfAppeal"
            :inputComponent="TextArea"
            :inputAttrs="{ placeholder: 'Describe the reason for your appeal', maxlength: 3000, rows: 5 }"
          />
          <div class="char-count">
            {{ currentSummaryLength }} / 3000 characters used
          </div>
        </MnColumn>
      </MnRow>
    </fieldset>

    <br/>

    <fieldset>
      <legend>Supporting Documents</legend>

      <p>If possible, attach copies of any relevant documents such as referrals, denials, prior authorizations, bills, explanation of benefits, screen shots, and written correspondence with your HMO or provider that are relevant to your external appeal. For example, you can upload any of the following:</p>
      <ul>
        <li>A statement describing the appeal.</li>
        <li>Statements and/or letters of medical necessity from your healthcare provider(s)</li>
        <li>A copy of the most recent denial letter from your insurer. Any supporting documents (e.g., medical records, peer-reviewed studies, photos, etc.)</li>
      </ul>
      <p>Microsoft Word, PDF, Jpeg, and PNG files are allowed.</p>

      <Checkbox
        inputId="isNothingToUpload"
        v-model="isNothingToUpload"
        :binary="true"
      />
      <label for="isNothingToUpload" class="mb-0 mx-2">I don't have anything to upload.</label>

      <MnRow v-if="!isNothingToUpload">
        <MnColumn cols="6">
          <MNFileUpload
            module-type="External Appeal Supporting Documents"
            ref="externalAppealSupportingDocuments"
            name="externalAppealSupportingDocuments"
            accept="application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf,image/png,image/jpeg"
            :fileLimit="20"/>
        </MnColumn>
      </MnRow>
    </fieldset>

    <br/>

    <fieldset>
      <legend>Information About Your Appeal</legend>
      <p>MDH will review your information and evaluate whether an investigation should be conducted before filing an external appeal. If MDH believes an investigation should be conducted, we will contact you directly to discuss our investigation process and obtain any necessary information. Please indicate if any of the below apply: </p>
      <MnRow>
        <MnColumn cols="4">
          <ValidatedInput
            id="internalAppealStatus"
            label="Internal Appeal Status"
            v-model="form.state.data.internalAppealStatus"
            :validation="form.state.$v.internalAppealStatus"
            :inputComponent="Select"
            :inputAttrs="{
              inputId: 'internalAppealStatus',
              ariaLabel: 'Internal Appeal Status',
              options: internalAppealOptions,
              optionLabel: 'value',
              optionValue: 'key',
              placeholder: 'Select Internal Appeal Status'
            }"
          />
        </MnColumn>

        <MnColumn v-if="form.state.data.internalAppealStatus && form.state.data.internalAppealStatus === 'Filed'" cols="4">
          <ValidatedInput
            id="internalAppealDenialDate"
            label="Internal Appeal Denial Date"
            v-model="form.state.data.internalAppealDenialDate"
            :inputComponent="DatePicker"
            :inputAttrs="{
              inputId: 'internalAppealDenialDate',
              ariaLabel: 'Internal Appeal Denial Date',
              placeholder: 'MM/DD/YYYY',
              dateFormat: 'mm/dd/yy',
              showIcon: true,
              showButtonBar: true,
              class: 'form-control mb-2',
              maxDate: new Date()
            }"
          />
        </MnColumn>
      </MnRow>
      <MnRow>
        <MnColumn cols="12">
          <div class="d-flex align-items-center mb-2">
            <Checkbox
              inputId="isExternalAppealOnly"
              v-model="form.state.data.isExternalAppealOnly"
              :binary="true"
            />
            <label for="isExternalAppealOnly" class="mb-0 mx-2">I only want to file an external appeal. I understand that if I file an appeal, the decision reached by the independent review organization (IRO) is final and MDH cannot investigate my complaint afterwards.</label>
          </div>
          <p>A normal appeal can take 45 days. If you believe a 45-day wait could harm your health, or the health of the person you are representing, you may get an expedited 72-hour appeal pursuant to Minnesota Statute 62Q.73, Subd. 6. (e) (1).</p>
          <div class="d-flex align-items-center mb-2">
            <Checkbox
              inputId="isExpedited"
              v-model="form.state.data.isExpedited"
              :binary="true"
            />
            <label for="isExpedited" class="mb-0 mx-2">I would like to expedite my appeal (Additional information required).</label>
          </div>
        </MnColumn>
      </MnRow>
    </fieldset>

    <br/>

    <fieldset v-if="form.state.data.isExpedited">
      <legend>Provider Information</legend>

      <MnRow>
        <MnColumn cols="4">
          <ValidatedInput
            id="name"
            label="Name"
            v-model="form.state.data.provider.name"
            :validation="form.state.$v.provider.name"
            :inputComponent="InputText"
            :inputAttrs="{ placeholder: 'Provider Name' }"
          />
        </MnColumn>

        <MnColumn cols="4">
          <ValidatedInput
            id="representative"
            label="Representative"
            v-model="form.state.data.provider.representative"
            :inputComponent="InputText"
            :inputAttrs="{ placeholder: 'Provider Representative/Contact Person' }"
          />
        </MnColumn>

        <MnColumn cols="4">
          <ValidatedInput
            id="phone"
            label="Provider Phone Number"
            v-model="form.state.data.provider.phoneNumber"
            :validation="form.state.$v.provider.phoneNumber"
            :inputComponent="InputMask"
            :inputAttrs="{ placeholder: 'Provider Phone Number', mask: '(999) 999-9999' }"
          />
        </MnColumn>

        <MnColumn cols="4">
          <ValidatedInput
            id="email"
            label="Provider Email Address"
            v-model="form.state.data.provider.email"
            :validation="form.state.$v.provider.email"
            :inputComponent="InputText"
            :inputAttrs="{ placeholder: 'Provider Email' }"
          />
        </MnColumn>
      </MnRow>
    </fieldset>

    <br/>

    <fieldset v-if="form.state.data.isExpedited">
      <legend>Provider Address</legend>
      <MnRow>
        <MnColumn cols="8">
          <ValidatedInput
            id="street"
            label="Street"
            v-model="form.state.data.provider.street"
            :validation="form.state.$v.provider.street"
            :inputComponent="InputText"
            :inputAttrs="{ placeholder: 'Street' }"
          />
        </MnColumn>
      </MnRow>

      <MnRow>
        <MnColumn cols="4">
          <ValidatedInput
            id="city"
            label="City"
            v-model="form.state.data.provider.city"
            :validation="form.state.$v.provider.city"
            :inputComponent="InputText"
            :inputAttrs="{ placeholder: 'City' }"
          />
        </MnColumn>

        <MnColumn cols="4">
          <ValidatedInput
            id="state"
            label="State"
            v-model="form.state.data.provider.state"
            :validation="form.state.$v.provider.state"
            :inputComponent="InputText"
            :inputAttrs="{ placeholder: 'State' }"
          />
        </MnColumn>

        <MnColumn cols="4">
          <ValidatedInput
            id="zipCode"
            label="Zip Code"
            v-model="form.state.data.provider.zipCode"
            :validation="form.state.$v.provider.zipCode"
            :inputComponent="InputMask"
            :inputAttrs="{ placeholder: 'Zip Code', mask: '99999?-9999' }"
          />
        </MnColumn>
      </MnRow>
    </fieldset>

    <br/>

    <fieldset>
      <legend>Forwarding Authorization</legend>

      <p>If it is determined that the external appeal does not fall under the jurisdiction of MDH, we may forward it to the appropriate state agency for follow up (Commerce, Human Services or Minnesota Management and Budget).
        Please indicate if you authorize MDH to share your external appeal with another state agency.</p>

      <MnRow>
        <MnColumn cols="4">
          <ValidatedInput
            id="isForwardingAuthorized"
            label="Forwarding Authorization"
            v-model="form.state.data.isForwardingAuthorized"
            :validation="form.state.$v.isForwardingAuthorized"
            :inputComponent="Select"
            :inputAttrs="{
              inputId: 'isForwardingAuthorized',
              ariaLabel: 'Authorize Forwarding',
              options: isForwardingAuthorizedOptions,
              optionLabel: 'value',
              optionValue: 'key',
              placeholder: 'Select an Answer'
            }"
          />
        </MnColumn>
      </MnRow>
    </fieldset>

    <br/>

    <fieldset>
      <legend>Signature, Attestation and Consent to Release Information</legend>

      <p>I certify that all the information in this form is true to the best of my knowledge. I am insured by the above health plan company.
        To go through an external appeal, I understand that I need to have gone through my health plan company’s internal appeal process.
        I authorize my health plan company and my medical providers to release my medical records to the Minnesota Department of Health solely for the purpose of reviewing my complaint.
        This consent will be revoked upon the conclusion of the investigation or external appeal.
        I authorize the Minnesota Department of Health to forward any documents connected to my external appeal to a state-approved IRO contract vendor.</p>
      <p>I understand it may be necessary for MDH to obtain copies of protected health information for MDH to fully investigate my complaint.
        My signature below is my signed permission allowing MDH to obtain this information.</p>
      <p>If an authorized representative is designated above, that person is authorized to represent me in the external appeal process.</p>
      <p>By typing my name in the signature field, I understand that I am electronically signing this form. I attest and certify that the information provided is true and accurate.
        I understand that my electronic signature has the same legal effect and can be enforced in the same way as a handwritten signature. (MN Statutes, Chapter 325L.07)</p>

      <MnRow>
        <MnColumn cols="4">
          <ValidatedInput
            id="signature"
            label="Signature"
            v-model="form.state.data.signature"
            :validation="form.state.$v.signature"
            :inputComponent="InputText"
            :inputAttrs="{ placeholder: 'Your Full Name' }"
          />
        </MnColumn>

        <MnColumn cols="8">
          <ReadOnlyText label="Date" :value="todayDate">{{ todayDate }}</ReadOnlyText>
        </MnColumn>

        <MnColumn cols="4" v-if="form.state.data.patient.relationshipToPatient && form.state.data.patient.relationshipToPatient !== EXTERNAL_APPEALS_PATIENT_RELATIONSHIPS.Self">
          <ValidatedInput
            id="signatureRelationship"
            label="Relationship to Patient"
            v-model="form.state.data.signatureRelationship"
            :validation="form.state.$v.signatureRelationship"
            :inputComponent="InputText"
            :inputAttrs="{ placeholder: 'Relationship' }"
          />
        </MnColumn>
      </MnRow>
    </fieldset>

    <hr/>
    <Button type="submit" label="Submit Appeal"/>
  </form>
  <Dialog v-model:visible="isSubmissionCompleteOpen" modal header="Submission Complete" :closable="false" :style="{ width: '30rem' }">
    <div class="row mb-8">
      <div class="col-2">
        <i :class="[ICONS.CheckCircle, 'color-success', 'fs-1']"></i>
      </div>
      <div class="col-10">
        <p>
          Thank you for submitting your external appeal. The reference number for your external appeal is #<b>{{ savedForm.appealId }}</b> Please keep this appeal/complaint number for your records. You will also receive an email confirmation about your external appeal. Please refer to the instructions in the email for further steps.
        </p>
      </div>
    </div>
    <template #footer>
      <div class="d-flex justify-content-end">
        <Button type="button" class="me-2" rounded raised @click="closeDialog(); router.push({path: '/', force: true});" label="Done" severity="secondary"/>
      </div>
    </template>
  </Dialog>
  <Dialog v-model:visible="isSubmissionErrorOpen" modal header="Submission Error" :closable="false" :style="{ width: '30rem' }">
    <div class="row mb-8">
      <div class="col-2">
        <i :class="[ICONS.ErrorCircle, 'color-danger', 'fs-1']"></i>
      </div>
      <div class="col-10">
        <p>
          An error has occurred with submitting the form.
        </p>
      </div>
    </div>
    <template #footer>
      <div class="d-flex justify-content-end">
        <Button type="button" class="me-2" rounded raised @click="closeDialog();" label="Done" severity="secondary"/>
      </div>
    </template>
  </Dialog>
</template>

<script setup>
import InputText from 'primevue/inputtext';
import InputMask from 'primevue/inputmask';
import DatePicker from 'primevue/datepicker';
import TextArea from 'primevue/textarea';
import Button from 'primevue/button';
import Checkbox from 'primevue/checkbox';
import PageHeader from "@/components/common/PageHeader.vue";
import {ExternalAppealModel} from "@/models/external_appeals/ExternalAppealModel.js";
import MnRow from "@/components/common/MnRow.vue";
import MnColumn from "@/components/common/MnColumn.vue";
import {useForm} from "@/composables/form.js";
import {computed, nextTick, ref} from "vue";
import ValidatedInput from "@/components/common/ValidatedInput.vue";
import {
  EXTERNAL_APPEALS_FORWARD_AUTHORIZATION_OPTIONS,
  EXTERNAL_APPEALS_HEALTH_INSURANCE_COMPANIES,
  EXTERNAL_APPEALS_INTERNAL_APPEAL_CONFIRMATION_OPTIONS,
  EXTERNAL_APPEALS_PATIENT_RELATIONSHIPS,
  EXTERNAL_APPEALS_TYPE_OF_COVERAGES,
  ICONS, TOAST_SAVE_LIFE
} from "@/utils/constants.js";
import Select from "primevue/select";
import ReadOnlyText from "@/components/common/ReadOnlyText.vue";
import {submitAppeal} from '@/services/externalAppealService.js';
import {sendAppealConfirmationEmail} from '@/services/emailService.js';
import Dialog from "primevue/dialog";
import {useDialogStore} from "@/stores/dialogStore.js";
import router from "@/router/index.js";
import MNFileUpload from "@/components/common/MNFileUpload.vue";
import {useToast} from "primevue/usetoast";
import {externalAppealValidationRules} from "@/validators/external_appeals/ExternalAppealValidator.js";

const model = new ExternalAppealModel();
const form = useForm(model, (state) => externalAppealValidationRules(state));
const savedForm = ref(null);
const externalAppealSupportingDocuments = ref();
const isNothingToUpload = ref(false);
const toast = useToast();

const dialogStore = useDialogStore();
const toggleDialog = (name) => dialogStore.toggle(name);
const closeDialog = () => dialogStore.close();
const isSubmissionCompleteOpen = computed(() => dialogStore.isOpen('submissionComplete'));
const isSubmissionErrorOpen = computed(() => dialogStore.isOpen('submissionError'));

const patientRelationshipOptions = computed(() => {
  return Object.entries(EXTERNAL_APPEALS_PATIENT_RELATIONSHIPS).map(([k, v]) => ({
    key: k,
    value: v
  }));
});

const healthInsuranceCompanyOptions = computed(() => {
  return Object.entries(EXTERNAL_APPEALS_HEALTH_INSURANCE_COMPANIES).map(([k, v]) => ({
    key: k,
    value: v
  }));
});

const typeOfCoverageOptions = computed(() => {
  return Object.entries(EXTERNAL_APPEALS_TYPE_OF_COVERAGES).map(([k, v]) => ({
    key: k,
    value: v
  }));
});

const internalAppealOptions = computed(() => {
  return Object.entries(EXTERNAL_APPEALS_INTERNAL_APPEAL_CONFIRMATION_OPTIONS).map(([k, v]) => ({
    key: k,
    value: v
  }));
});

const isForwardingAuthorizedOptions = computed(() => {
  return Object.entries(EXTERNAL_APPEALS_FORWARD_AUTHORIZATION_OPTIONS).map(([k, v]) => ({
    key: k,
    value: v
  }));
});

const currentSummaryLength = computed(() => {
  return form.state.$v.summaryOfAppeal.$model
    ? form.state.$v.summaryOfAppeal.$model.length
    : 0;
});

const todayDate = computed(() => {
    const today = new Date();
    return today.toLocaleDateString();
});

const onSubmit = async () => {
  form.state.$v.$touch();
  if (form.state.$v.$invalid) {
    const invalidFields = findVisibleInvalidFields(form.state.$v).join(', ');
    toast.add({
      group: 'top-toast',
      severity: 'error',
      summary: 'Invalid fields',
      detail: `Please update the following invalid fields: ${invalidFields}`,
      life: TOAST_SAVE_LIFE
    });
    await nextTick(() => form.focusInvalidInput());
  } else {
    await addUploadedFilesToModel();
    try {
      const formData = form.state.data;
      savedForm.value = await form.save(() => submitAppeal(form.state.data));
      if (savedForm.value && savedForm.value.appealId) {
        toggleDialog('submissionComplete');
        // Send appeal confirmation email after the appeal submission.
        console.log('Sending appeal confirmation email to : ', formData.patient.email);
        console.log('Appeal ID: ', savedForm.value.appealId);
        if (formData.patient?.email?.trim()) {
          await sendAppealConfirmationEmail(formData.patient.email, savedForm.value.appealId);
        }
      }
    } catch (e) {
      toggleDialog('submissionError');
    }
  }
};

const findVisibleInvalidFields = ($v, parentKey = '') => {
  const invalidFields = [];

  Object.entries($v).forEach(([key, val]) => {
    const fieldKey = parentKey ? `${parentKey}.${key}` : key;

    // If this is a leaf field with a $model
    if (val?.$invalid && val?.$model !== undefined) {
      invalidFields.push(fieldKey);
    }

    // If it's a nested validation object, recurse (ignore $-prefixed keys)
    else if (typeof val === 'object' && !key.startsWith('$')) {
      invalidFields.push(...findVisibleInvalidFields(val, fieldKey));
    }
  });

  return invalidFields;
};

const addUploadedFilesToModel = async () => {
  const files = await externalAppealSupportingDocuments.value?.getUploadedFiles?.();
  if (!files || !Array.isArray(files)) return [];

  form.state.data.documents = files.map(file => ({
    type: file.type,
    url: file.name,
  }));

  return files;
};
</script>

<style scoped>
.char-count {
  font-size: 0.875rem;
  color: #555;
  margin-top: 4px;
  text-align: right;
}
</style>